import { useState } from "react";
import { Row, Col, Card, CardBody, CardTitle, CardText, Button, Badge, Form, FormGroup, Label, Input } from "reactstrap";
import Swal from "sweetalert2";
import { ESTADOS } from "../shared/EstadosMexico";

const initialDatosFacturacion = {
    rfc: '',
    razonSocial: '',
    regimenFiscal: '',
    codigoPostal: '',
    calle: '',
    colonia: '',
    municipio: '',
    estado: '',
    usoCfdi: '',
    correoFacturacion: ''
};

const regimenesFiscales = [
    { value: '601', label: '601 — General de Ley Personas Morales' },
    { value: '603', label: '603 — Personas Morales con Fines no Lucrativos' },
    { value: '605', label: '605 — Sueldos y Salarios e Ingresos Asimilados a Salarios' },
    { value: '606', label: '606 — Arrendamiento' },
    { value: '612', label: '612 — Personas Físicas con Actividades Empresariales y Profesionales' },
    { value: '616', label: '616 — Sin obligaciones fiscales' },
    { value: '621', label: '621 — Incorporación Fiscal' },
    { value: '625', label: '625 — Régimen de las Actividades Empresariales con ingresos a través de Plataformas Tecnológicas' },
    { value: '626', label: '626 — Régimen Simplificado de Confianza' }
];

const usosCfdi = [
    { value: 'G03', label: 'G03 — Gastos en general' },
    { value: 'S01', label: 'S01 — Sin efectos fiscales' },
    { value: 'CP01', label: 'CP01 — Pagos' },
    { value: 'D04', label: 'D04 — Donativos' }
];

const paquetes = [
    {
        id: 'basico',
        nombre: 'Paquete Básico',
        precio: 2490,
        descripcion: 'Cumplimiento esencial para una unidad de negocio en arranque.',
        servicios: [
            '1 unidad de negocio',
            'Expedientes PLD y umbrales de identificación',
            'Registro de operaciones y clientes',
            'Alertas básicas de cumplimiento',
            'Soporte por correo en horario laboral'
        ]
    },
    {
        id: 'pro',
        nombre: 'Paquete Pro',
        precio: 4990,
        destacado: true,
        descripcion: 'Operación multiunidad con automatización y listas restrictivas.',
        servicios: [
            'Todo lo incluido en Básico',
            'Hasta 5 unidades de negocio',
            'Motor Q-Detect (UIF / SAT / ONU)',
            'Scoring de riesgo y acumulado a 6 meses',
            'Reportes operativos y exportación',
            'Soporte prioritario'
        ]
    },
    {
        id: 'enterprise',
        nombre: 'Paquete Enterprise',
        precio: 8990,
        descripcion: 'Escala corporativa, auditoría y controles avanzados.',
        servicios: [
            'Todo lo incluido en Pro',
            'Unidades de negocio ilimitadas',
            'Usuarios, roles y permisos avanzados',
            'Bitácora de auditoría y dictamen anual asistido',
            'Integraciones API y acompañamiento REC',
            'Soporte dedicado y capacitación anual incluida'
        ]
    }
];

function formatearPrecio(monto) {
    return new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency: 'MXN',
        maximumFractionDigits: 0
    }).format(monto);
}

export default function PagosFacturacion() {
    const [paqueteSeleccionado, setPaqueteSeleccionado] = useState('pro');
    const [datosFacturacion, setDatosFacturacion] = useState(initialDatosFacturacion);

    function handleFacturacionChange(name) {
        return function (e) {
            setDatosFacturacion({ ...datosFacturacion, [name]: e.target.value });
        };
    }

    function handleGuardarFacturacion(e) {
        e.preventDefault();
        console.log('Datos de facturación (solo frontend):', datosFacturacion);
        Swal.fire({
            icon: 'success',
            title: 'Datos guardados (simulado)',
            text: 'Tus datos de facturación quedaron registrados localmente. La emisión de CFDI se conectará al backend.',
            confirmButtonText: 'Continuar',
            confirmButtonColor: '#6c757d'
        });
    }

    function handlePagarStripe(paquete) {
        Swal.fire({
            icon: 'info',
            title: 'Pago con Stripe (simulado)',
            html: `Se iniciaría el checkout de Stripe para <strong>${paquete.nombre}</strong> (${formatearPrecio(paquete.precio)}/mes).<br/>Integración pendiente en backend.`,
            confirmButtonText: 'Entendido',
            confirmButtonColor: '#6c757d'
        });
        console.log('Checkout Stripe (dummy):', {
            paqueteId: paquete.id,
            monto: paquete.precio,
            moneda: 'MXN',
            datosFacturacion
        });
    }

    return (
        <div>
            <h3>Pagos y facturación</h3>
            <Row>
                <Col>
                    <p>Elige el servicio que desees contratar y procede al pago mediante Stripe.</p>
                </Col>
            </Row>

            <Row className="g-3 mb-4">
                {paquetes.map((paquete) => {
                    const seleccionado = paqueteSeleccionado === paquete.id;
                    return (
                        <Col md={4} key={paquete.id}>
                            <Card
                                className={`h-100 ${seleccionado ? 'border-secondary' : ''}`}
                                outline={seleccionado}
                                color={seleccionado ? 'secondary' : undefined}
                            >
                                <CardBody className="d-flex flex-column">
                                    <div className="mb-2">
                                        {paquete.destacado && (
                                            <Badge className="mb-2 text-white bg-primary">Recomendado</Badge>
                                        )}
                                        <CardTitle tag="h5">{paquete.nombre}</CardTitle>
                                        <CardText className="text-muted small">{paquete.descripcion}</CardText>
                                        <p className="fs-4 mb-0">
                                            {formatearPrecio(paquete.precio)}
                                            <span className="fs-6 text-muted"> / mes</span>
                                        </p>
                                    </div>
                                    <ul className="small flex-grow-1 ps-3">
                                        {paquete.servicios.map((servicio) => (
                                            <li key={servicio} className="mb-1">{servicio}</li>
                                        ))}
                                    </ul>
                                    <Button
                                        color={seleccionado ? 'secondary' : 'outline-secondary'}
                                        className="mt-3"
                                        onClick={() => setPaqueteSeleccionado(paquete.id)}
                                    >
                                        {seleccionado ? 'Paquete seleccionado' : 'Seleccionar paquete'}
                                    </Button>
                                    <Button
                                        color="dark"
                                        className="mt-2"
                                        onClick={() => handlePagarStripe(paquete)}
                                    >
                                        Pagar con Stripe
                                    </Button>
                                </CardBody>
                            </Card>
                        </Col>
                    );
                })}
            </Row>

            <Row className="mt-2">
                <Col>
                    <p className="text-muted small">
                        Si proporcionas tus datos para facturación, generaremos una factura mensual automáticamente.
                    </p>
                </Col>
            </Row>

            <Row className="encuadrado">
                <Col md={12}>
                    <h5 className="mb-3">Datos de facturación</h5>
                    <Form onSubmit={handleGuardarFacturacion}>
                        <Row>
                            <Col md={6}>
                                <FormGroup>
                                    <Label for="rfc">RFC</Label>
                                    <Input
                                        type="text"
                                        id="rfc"
                                        value={datosFacturacion.rfc}
                                        onChange={handleFacturacionChange('rfc')}
                                        maxLength={13}
                                        placeholder="XAXX010101000"
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup>
                                    <Label for="razonSocial">Razón social o nombre fiscal</Label>
                                    <Input
                                        type="text"
                                        id="razonSocial"
                                        value={datosFacturacion.razonSocial}
                                        onChange={handleFacturacionChange('razonSocial')}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={6}>
                                <FormGroup>
                                    <Label for="regimenFiscal">Régimen fiscal</Label>
                                    <Input
                                        type="select"
                                        id="regimenFiscal"
                                        value={datosFacturacion.regimenFiscal}
                                        onChange={handleFacturacionChange('regimenFiscal')}
                                    >
                                        <option value="">Seleccionar...</option>
                                        {regimenesFiscales.map((regimen) => (
                                            <option key={regimen.value} value={regimen.value}>{regimen.label}</option>
                                        ))}
                                    </Input>
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup>
                                    <Label for="usoCfdi">Uso de CFDI</Label>
                                    <Input
                                        type="select"
                                        id="usoCfdi"
                                        value={datosFacturacion.usoCfdi}
                                        onChange={handleFacturacionChange('usoCfdi')}
                                    >
                                        <option value="">Seleccionar...</option>
                                        {usosCfdi.map((uso) => (
                                            <option key={uso.value} value={uso.value}>{uso.label}</option>
                                        ))}
                                    </Input>
                                </FormGroup>
                            </Col>
                        </Row>
                        <Row>
                        <Col md={6}>
                        <FormGroup>
                            <Label for="correoFacturacion">Correo electrónico para envío de factura</Label>
                            <Input
                                type="email"
                                id="correoFacturacion"
                                value={datosFacturacion.correoFacturacion}
                                onChange={handleFacturacionChange('correoFacturacion')}
                            />
                        </FormGroup>
                        </Col>
                        </Row>
                        <Row>
                            <Col md={6}>
                                <FormGroup>
                                    <Label for="calle">Calle y número</Label>
                                    <Input
                                        type="text"
                                        id="calle"
                                        value={datosFacturacion.calle}
                                        onChange={handleFacturacionChange('calle')}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup>
                                    <Label for="codigoPostal">Código postal</Label>
                                    <Input
                                        type="text"
                                        id="codigoPostal"
                                        value={datosFacturacion.codigoPostal}
                                        onChange={handleFacturacionChange('codigoPostal')}
                                        maxLength={5}
                                    />
                                </FormGroup>
                            </Col>
                            </Row>
                            <Row>
                            <Col md={4}>
                                <FormGroup>
                                    <Label for="colonia">Colonia</Label>
                                    <Input
                                        type="text"
                                        id="colonia"
                                        value={datosFacturacion.colonia}
                                        onChange={handleFacturacionChange('colonia')}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={4}>
                                <FormGroup>
                                    <Label for="municipio">Municipio o alcaldía</Label>
                                    <Input
                                        type="text"
                                        id="municipio"
                                        value={datosFacturacion.municipio}
                                        onChange={handleFacturacionChange('municipio')}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={4}>
                                <FormGroup>
                                    <Label for="estado">Estado</Label>
                                    <Input
                                        type="select"
                                        id="estado"
                                        value={datosFacturacion.estado}
                                        onChange={handleFacturacionChange('estado')}
                                    >
                                        <option value="">Seleccionar...</option>
                                        {ESTADOS.map((estado) => (
                                            <option key={estado} value={estado}>{estado}</option>
                                        ))}
                                    </Input>
                                </FormGroup>
                            </Col>
                        </Row>
                        <Row className="text-center mt-3">
                            <Button color="secondary" type="submit">Guardar datos de facturación</Button>
                        </Row>
                    </Form>
                </Col>
            </Row>
        </div>
    );
}
